import { useState } from "react";
import { Link, useRoute } from "wouter";
import ConsoleLayout from "@/components/ConsoleLayout";
import { getDomainIcon } from "@/components/AgentCard";
import { ArrowLeft, Copy, Check, ChevronDown, ChevronUp, ExternalLink, Wrench, Database, BookOpen, Sparkles } from "lucide-react";
import { cn } from "@/lib/utils";
import { copyToClipboard } from "@/lib/utils";
import { useLanguage } from "@/contexts/LanguageContext";
import { toast } from "sonner";
import { mockScienceAgents, DOMAIN_CONFIG } from "@/data/mockScienceAgents";
import type { ToolGroup } from "@/data/mockScienceAgents";

function ToolCard({ tool, language }: { tool: ToolGroup["tools"][0]; language: string }) {
  return (
    <div className="sci-card p-4 hover:border-white/15 transition-colors">
      <div className="flex items-start gap-3">
        <div className="w-8 h-8 rounded-lg bg-blue-500/10 border border-blue-500/20 flex items-center justify-center flex-shrink-0 mt-0.5">
          <Wrench size={13} className="text-blue-400" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1">
            <code className="text-sm font-mono text-blue-400 font-medium">{tool.tool_key}</code>
            {tool.is_xtalpi_proprietary && (
              <span className="text-[9px] px-1.5 py-0.5 rounded-full bg-cyan-500/10 text-cyan-400 border border-cyan-500/20 font-semibold">XtalPi</span>
            )}
          </div>
          <div className="text-sm text-white font-medium mb-0.5">
            {language === "zh" ? tool.display_name : tool.display_name_en}
          </div>
          <div className="text-xs text-slate-500">
            {language === "zh" ? tool.description : tool.description_en}
          </div>
        </div>
      </div>
    </div>
  );
}

function ToolGroupSection({ group, language }: { group: ToolGroup; language: string }) {
  const [open, setOpen] = useState(true);
  return (
    <div className="mb-6">
      <button
        onClick={() => setOpen(!open)}
        className="flex items-center gap-2 mb-3 group"
      >
        <h3 className="text-base font-bold text-white" style={{ fontFamily: "Space Grotesk, sans-serif" }}>
          {language === "zh" ? group.group_name : group.group_name_en}
        </h3>
        <span className="text-xs text-slate-500 bg-white/5 px-2 py-0.5 rounded-full border border-white/8">
          {group.tools.length}
        </span>
        {open ? <ChevronUp size={14} className="text-slate-500" /> : <ChevronDown size={14} className="text-slate-500" />}
      </button>
      {open && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
          {[...group.tools].sort((a, b) => (b.is_xtalpi_proprietary ? 1 : 0) - (a.is_xtalpi_proprietary ? 1 : 0)).map((tool) => (
            <ToolCard key={tool.tool_key} tool={tool} language={language} />
          ))}
        </div>
      )}
    </div>
  );
}

export default function ScienceAgentDetail() {
  const { t, language } = useLanguage();
  const [, params] = useRoute("/console/science-agents/:agentKey");
  const agentKey = params?.agentKey;
  const agent = mockScienceAgents.find((a) => a.agent_key === agentKey);
  const [copied, setCopied] = useState(false);
  const [activeTab, setActiveTab] = useState<"tools" | "datasources" | "scenarios">("tools");

  if (!agent) {
    return (
      <ConsoleLayout title={t("sciAgents.title")}>
        <div className="p-6 text-center text-slate-500 text-sm">Not found</div>
      </ConsoleLayout>
    );
  }

  const config = DOMAIN_CONFIG[agent.domain];

  const handleCopy = async () => {
    const ok = await copyToClipboard(agent.agent_key);
    if (ok) { setCopied(true); toast.success(language === "zh" ? "已复制" : "Copied"); setTimeout(() => setCopied(false), 2000); }
  };

  const tabs = [
    { key: "tools" as const, label: t("sciAgents.tabTools"), icon: <Wrench size={14} />, count: agent.stats.tool_count },
    { key: "datasources" as const, label: t("sciAgents.tabDatasources"), icon: <Database size={14} />, count: agent.stats.datasource_count },
    { key: "scenarios" as const, label: t("sciAgents.tabScenarios"), icon: <BookOpen size={14} />, count: agent.stats.scenario_count },
  ];

  return (
    <ConsoleLayout
      title={agent.agent_key}
      actions={undefined}
    >
      <div className="p-6 space-y-6 overflow-y-auto">
        {/* Back */}
        <Link href="/console/science-agents" className="inline-flex items-center gap-1.5 text-xs text-slate-400 hover:text-white transition-colors">
          <ArrowLeft size={12} /> {t("sciAgents.backToList")}
        </Link>

        {/* Header card */}
        <div className="sci-card p-6">
          <div className="flex items-start gap-5">
            <div
              className="w-16 h-16 rounded-xl flex items-center justify-center border"
              style={{ background: `${config.color}15`, borderColor: `${config.color}30`, color: config.color }}
            >
              {getDomainIcon(config.icon, 28)}
            </div>
            <div className="flex-1">
              <h1 className="text-2xl font-bold text-white mb-1" style={{ fontFamily: "Space Grotesk, sans-serif" }}>
                {agent.agent_key}
              </h1>
              <p className="text-sm text-slate-400 mb-4">
                {language === "zh" ? agent.tagline : agent.tagline_en}
              </p>

              {/* Stats + model_key row */}
              <div className="flex items-center gap-3 flex-wrap mb-4">
                <div className="flex items-center gap-2 px-3 py-1.5 rounded-lg bg-white/3 border border-white/8">
                  <code className="text-xs font-mono text-slate-400">{agent.agent_key}</code>
                  <button onClick={handleCopy} className="text-slate-600 hover:text-slate-300 transition-colors">
                    {copied ? <Check size={11} className="text-emerald-400" /> : <Copy size={11} />}
                  </button>
                </div>
                <span className="flex items-center gap-1.5 text-xs text-slate-500 px-2.5 py-1 rounded-full border border-white/8 bg-white/3">
                  <Wrench size={11} /> {agent.stats.tool_count} {language === "zh" ? "工具" : "tools"}
                </span>
                <span className="flex items-center gap-1.5 text-xs text-slate-500 px-2.5 py-1 rounded-full border border-white/8 bg-white/3">
                  <Database size={11} /> {agent.stats.datasource_count} {language === "zh" ? "数据源" : "sources"}
                </span>
                <span className="flex items-center gap-1.5 text-xs text-slate-500 px-2.5 py-1 rounded-full border border-white/8 bg-white/3">
                  <Sparkles size={11} /> Anthropic
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* Tab navigation — pill style */}
        <div className="flex items-center gap-2">
          {tabs.map((tab) => (
            <button
              key={tab.key}
              onClick={() => setActiveTab(tab.key)}
              className={cn(
                "flex items-center gap-1.5 px-4 py-2 rounded-full text-sm font-medium border transition-colors",
                activeTab === tab.key
                  ? "bg-blue-500/15 text-blue-400 border-blue-500/30"
                  : "bg-white/3 text-slate-400 border-white/8 hover:border-white/15 hover:text-slate-200"
              )}
            >
              {tab.icon}
              {tab.label}
              {"count" in tab && tab.count != null && (
                <span className={cn("text-[10px] ml-0.5", activeTab === tab.key ? "text-blue-400/60" : "text-slate-600")}>{tab.count}</span>
              )}
            </button>
          ))}
        </div>

        {/* Tab content */}

        {/* ── Tools ── */}
        {activeTab === "tools" && (
          <div>
            {agent.tool_groups.map((group) => (
              <ToolGroupSection key={group.group_key} group={group} language={language} />
            ))}
          </div>
        )}

        {/* ── Datasources ── */}
        {activeTab === "datasources" && (
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
            {agent.datasources.map((ds) => (
              <div key={ds.name} className="sci-card p-5 hover:border-white/15 transition-colors">
                <div className="flex items-center gap-3 mb-3">
                  <div className="w-10 h-10 rounded-lg bg-blue-500/10 border border-blue-500/20 flex items-center justify-center text-base font-bold text-blue-400">
                    {ds.name.charAt(0)}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-bold text-white" style={{ fontFamily: "Space Grotesk, sans-serif" }}>{ds.name}</span>
                      {ds.url && (
                        <a href={ds.url} target="_blank" rel="noopener noreferrer" className="text-slate-500 hover:text-blue-400 transition-colors">
                          <ExternalLink size={12} />
                        </a>
                      )}
                    </div>
                    <span className="text-[10px] text-slate-600 px-1.5 py-0.5 rounded-full bg-white/5 border border-white/8 mt-1 inline-block">
                      {language === "zh" ? ds.category : ds.category_en}
                    </span>
                  </div>
                </div>
                <p className="text-xs text-slate-400 leading-relaxed">
                  {language === "zh" ? ds.description : ds.description_en}
                </p>
              </div>
            ))}
          </div>
        )}

        {/* ── Scenarios ── */}
        {activeTab === "scenarios" && (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            {agent.scenarios.map((scenario, i) => (
              <div key={i} className="sci-card p-5 hover:border-white/15 transition-colors">
                <h4 className="text-base font-bold text-white mb-3" style={{ fontFamily: "Space Grotesk, sans-serif" }}>
                  {language === "zh" ? scenario.title : scenario.title_en}
                </h4>

                {/* User query */}
                <div className="rounded-lg bg-white/3 border border-white/5 px-4 py-3 mb-4">
                  <div className="text-[10px] text-slate-600 uppercase tracking-wider mb-1">{language === "zh" ? "示例提问" : "Example Query"}</div>
                  <p className="text-sm text-slate-300 italic">
                    "{language === "zh" ? scenario.user_query : scenario.user_query_en}"
                  </p>
                </div>

                {/* Workflow steps */}
                <div className="mb-4">
                  <div className="text-xs font-semibold text-slate-400 mb-2">{language === "zh" ? "工作流步骤" : "Workflow Steps"}</div>
                  <div className="space-y-2">
                    {(language === "zh" ? scenario.workflow_steps : scenario.workflow_steps_en).map((step, j) => (
                      <div key={j} className="flex items-center gap-3">
                        <span className="w-6 h-6 rounded-full bg-blue-500/10 border border-blue-500/20 flex items-center justify-center text-[11px] font-bold text-blue-400 flex-shrink-0">
                          {j + 1}
                        </span>
                        <span className="text-sm text-slate-300">{step}</span>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Tools used */}
                <div className="flex flex-wrap gap-1.5">
                  {scenario.tools_used.map((tool) => (
                    <code key={tool} className="text-[10px] px-2 py-0.5 rounded-full bg-blue-500/10 text-blue-400 border border-blue-500/20 font-mono">
                      {tool}
                    </code>
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </ConsoleLayout>
  );
}
